/**
 * BOOK WARNINGS banner (ADR-0042 revision 2026-09-06).
 *
 * The problem it solves: a CSV whose third line is malformed renders 18 of 19
 * holdings and looks *entirely correct*. Nothing on screen is wrong — a lot is
 * simply absent, and absence has no visual signature. The warnings existed only
 * in the pino log, which is precisely where an operator reading their own
 * screen is not looking.
 *
 * Distinct from `StaleDataBanner`: that reports the market data being old,
 * which is a property of ingest. This reports the operator's own file being
 * partly unreadable, which only they can fix — hence amber rather than the
 * `--down` red, and no runbook link, because the fix is "edit your CSV".
 *
 * The live region is ALWAYS MOUNTED, fresh or not, for the reason spelled out
 * in `stale-data-banner.tsx`: a region inserted in the same commit as its text
 * is not reliably announced, and warnings arrive when the book query settles —
 * a runtime flip, exactly the case the announcement exists for.
 */
import styles from './book-warnings-banner.module.css';

import type { JSX } from 'react';

export interface BookWarningsBannerProps {
  /** Parse warnings from `/v1/book`; empty is the healthy state. */
  readonly warnings: readonly string[];
}

/**
 * Deliberately not "Book warnings": the region is announced in both states and
 * its own name must not duplicate the text it wraps, or the reading becomes
 * "Book warnings, Book warnings, …" (same rule as the staleness region).
 */
const REGION_LABEL = 'Manual book status';

/**
 * One warning is quoted in full; several are counted with the first shown. The
 * whole list is in the title so it is recoverable without a console, but the
 * banner does not grow to twelve lines and push the screen down.
 */
function summarise(warnings: readonly string[], head: string): string {
  const extra = warnings.length - 1;
  return extra > 0 ? `${head} (+${String(extra)} more)` : head;
}

export function BookWarningsBanner({ warnings }: BookWarningsBannerProps): JSX.Element {
  const head = warnings[0];

  if (head === undefined) {
    return (
      <div className="visually-hidden" role="status" aria-live="polite" aria-label={REGION_LABEL} />
    );
  }

  return (
    <div
      className={styles['banner']}
      role="status"
      aria-live="polite"
      aria-label={REGION_LABEL}
      data-book-warning="true"
    >
      <span className={styles['label']}>Book</span>
      <span className={styles['detail']} title={warnings.join('\n')}>
        {summarise(warnings, head)}
      </span>
    </div>
  );
}
